<template>
  <component
    :is="resolvedTag"
    class="play-list-item"
    :class="[
      `play-list-item--${variant}`,
      {
        'play-list-item--action': isAction,
        'is-active': active,
        'is-disabled': disabled,
      },
    ]"
    :href="resolvedTag === 'a' ? (href || null) : null"
    :to="resolvedTag === 'router-link' ? to : null"
    :type="resolvedTag === 'button' ? 'button' : null"
    :disabled="resolvedTag === 'button' ? disabled : null"
    :aria-current="active ? 'true' : null"
    :aria-disabled="disabled ? 'true' : null"
    v-bind="$attrs"
    v-on="$listeners"
  >
    <!-- VUE3-MIGRATION: $listeners removed in Vue 3 (merged into $attrs).
       On migration delete `v-on="$listeners"` — v-bind="$attrs" forwards listeners too. -->
    <slot />
  </component>
</template>

<script>
/**
 * PlayListItem — one row inside <PlayList>.
 *
 * Token-driven (see src/styles/tokens.scss). Replaces bootstrap-vue's
 * <b-list-group-item>. A static row by default, or an actionable one when
 * `button`, `href`, or `to` is set (renders <button>/<a>/<router-link> with
 * hover + active states). `variant` tints the active/hover surface; `active`
 * marks the current selection.
 *
 * VUE3-MIGRATION: `v-on="$listeners"` is the only Vue 2-ism; drop it on Vue 3
 * (v-bind="$attrs" keeps forwarding listeners). Props/slots are Vue 3-safe.
 */
export default {
  name: 'PlayListItem',
  inheritAttrs: false,
  props: {
    /** Force an actionable <button> */
    button: {
      type: Boolean,
      default: false,
    },
    href: {
      type: String,
      default: '',
    },
    to: {
      type: [String, Object],
      default: null,
    },
    active: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    variant: {
      type: String,
      default: 'primary',
      validator: v => ['primary', 'secondary', 'highlight', 'accent', 'danger'].includes(v),
    },
  },
  computed: {
    isAction() {
      return this.button || !!this.href || !!this.to
    },
    resolvedTag() {
      if (this.to) return 'router-link'
      if (this.href) return 'a'
      if (this.button) return 'button'
      return 'div'
    },
  },
}
</script>

<style lang="scss" scoped>
.play-list-item {
  --li-on: var(--c-primary);
  --li-on-soft: var(--c-primary-soft);
  --li-on-strong: var(--c-primary-strong);
  display: block;
  width: 100%;
  padding: 14px 18px;
  font-family: var(--font-body);
  color: var(--text-body);
  text-align: left;
  text-decoration: none;
  background: transparent;
  border: 0;

  & + & { border-top: var(--border-w) solid var(--line); }

  &--secondary { --li-on: var(--c-secondary); --li-on-soft: var(--c-secondary-soft); --li-on-strong: var(--c-secondary-strong); }
  &--highlight { --li-on: var(--c-highlight); --li-on-soft: var(--c-highlight-soft); --li-on-strong: var(--c-highlight-strong); }
  &--accent    { --li-on: var(--c-accent);    --li-on-soft: var(--c-accent-soft);    --li-on-strong: var(--c-accent-strong); }
  &--danger    { --li-on: var(--c-danger);    --li-on-soft: var(--c-danger-soft);    --li-on-strong: var(--c-danger-strong); }

  /* ── Actionable ── */
  &--action {
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    transition: background var(--dur-fast) var(--ease-out), color var(--dur-fast) var(--ease-out);

    &:hover:not(.is-disabled) {
      background: var(--li-on-soft);
      color: var(--li-on-strong);
    }

    &:focus-visible {
      outline: 2px solid var(--li-on);
      outline-offset: -2px;
    }
  }

  &.is-active {
    background: var(--li-on);
    color: var(--c-primary-on);
    font-weight: 600;
  }

  &.is-disabled {
    opacity: .55;
    pointer-events: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .play-list-item--action { transition: none; }
}
</style>
